import PrimaryInnerGlowButton from '@/components/buttons/primary';
import NextImage from '@/components/Image';
import Styled from '@/components/IntegrationsCloud/IntegrationsCloud.styled';
import Link from '@/components/Link';
import gtm from '@/lib/gtm';
import { maybeNotSet } from '@/lib/typescript-helpers';
import { FC, ReactElement } from 'react';

interface IntegrationsCloudProps {
  heading: string;
  text: ReactElement;
  cta?: {
    text: string;
    url: string;
  };
}

const integrations = [
  'Intercom',
  'Whatsapp',
  'Salesforce',
  'Slack',
  'Asana',
  'Jira',
  'Zoom',
  'Shopify',
  'Gmail',
  'Zapier',
] as const;

const delays = [3.5, 1.8, 2.3, 1.3, 1.0, 2.4, 3.8, 1.7, 3.9, 2.8];

const isStorybook = process.env.STORYBOOK === 'true';

/**
 * Returns the image URL for an integration logo bubble (`NextImage`).
 *
 * **Normal app (`yarn dev`, production)** — Uses `/staticwebassets/...`. The Next server applies
 * rewrites (`src/next-config/rewrites.js`) so those paths are forwarded to CloudFront; the browser
 * still requests your dev origin (e.g. port 1337) and gets the real JPEG from the CDN.
 *
 * **Storybook** — Runs on a different dev server (e.g. port 6006). Root-relative `/staticwebassets`
 * URLs hit Storybook’s origin, which does not run Next’s rewrites, so they would 404. When
 * we are in storybook, we swap in a `placehold.co` URL instead
 *
 * @param name — Integration label (e.g. `"Slack"`); drives both the filename segment and placeholder text.
 */
const getIntegrationLogoSrc = (name: string) => {
  if (isStorybook) {
    return `https://placehold.co/200x200/png?text=${encodeURIComponent(name)}`;
  }

  return `/staticwebassets/modules/integrationscloud/${name.toLowerCase()}.jpg`;
};

const IntegrationsCloud: FC<IntegrationsCloudProps> = (props) => {
  return (
    <Styled.Wrapper>
      <Styled.ContentWrapper>
        <Styled.TopBubbleGroup>
          {integrations.slice(0, 5).map((bubble, index) => (
            <Styled.Bubble key={bubble} $delay={delays[index]}>
              <NextImage
                src={getIntegrationLogoSrc(bubble)}
                width={200}
                height={200}
                sizes="200px"
                alt={`${bubble} logo`}
              />
            </Styled.Bubble>
          ))}
        </Styled.TopBubbleGroup>

        <Styled.Heading id="integrations-cloud-heading">{props.heading}</Styled.Heading>

        <Styled.Text id="integrations-cloud-text">{props.text}</Styled.Text>

        {props.cta && (
          <Styled.ButtonWrapper>
            <Link href={props.cta.url}>
              <PrimaryInnerGlowButton
                id="integrations-cloud-cta"
                as="a"
                onClick={() => {
                  gtm.track('click', {
                    element_type: 'button',
                    element_label: maybeNotSet(props.cta?.text),
                    location: 'Integrations Cloud Module',
                  });
                }}
              >
                {props.cta.text}
              </PrimaryInnerGlowButton>
            </Link>
          </Styled.ButtonWrapper>
        )}

        <Styled.BottomBubbleGroup>
          {integrations.slice(5).map((bubble, index) => (
            <Styled.Bubble key={bubble} $delay={delays[index + 5]}>
              <NextImage
                src={getIntegrationLogoSrc(bubble)}
                width={200}
                height={200}
                sizes="200px"
                alt={`${bubble} logo`}
              />
            </Styled.Bubble>
          ))}
        </Styled.BottomBubbleGroup>
      </Styled.ContentWrapper>
    </Styled.Wrapper>
  );
};

export default IntegrationsCloud;
